import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Rating, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import { useLazyGetRatingsQuery } from "../../services/main";
import { RatingRes } from "../../types/General";
import { isValidInput } from "../../utils/validations";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import moment from "moment";
import usePermission from "../../hooks/usePermission";

const ManageReviews = () => {
  const [ratingMethod] = useLazyGetRatingsQuery();

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState<number | null>(4);
  const [page, setPage] = useState<number>(1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [rows, setRows] = useState<RatingRes[]>([]);
  const [count, setCount] = useState(0);
  usePermission({label:"Manage Reviews",page_permission:"isView"})

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 220,
      field: "customer",
      headerName: "Customer Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-customers/detail/${row?.user?._id || ""}`)
            }
          >
            <figure>
              <img
                src={row?.user?.image || "/static/images/user_placeholder.png"}
                alt=""
              />
            </figure>
            <p>
              <b>{row?.user?.fullName || ""}</b>{" "}
              <span>{row?.user?.email || ""}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 220,
      field: "maintenance",
      headerName: "Maintenance Team Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(
                `/manage-maintenance-team/detail/${row?.serviceProvider?._id}`
              )
            }
          >
            <p>
              <b>{row?.serviceProvider?.fullName || ""}</b>{" "}
              <span>{row?.serviceProvider?.email || ""}</span>
            </p>
          </div>
        );
      },
    },
    {
      minWidth: 160,
      field: "rating",
      headerName: "Rating",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Rating
            name="read-only"
            value={row?.rating || 0}
            readOnly
            className="v2"
          />
        );
      },
    },
    {
      flex: 2,
      minWidth: 200,
      field: "review",
      headerName: "Review",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row?.review || ""} arrow>
            <div className="user_block">
              <p>{row?.review || ""}</p>
            </div>
          </Tooltip>
        );
      },
    },
    {
      minWidth: 150,
      field: "date",
      headerName: "Date",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{moment(row?.createdAt).format("DD MMM YYYY")}</p>
          </div>
        );
      },
    },
  ];

  const getAllList = async () => {
    setIsLoading(true);
    try {
      const response = await ratingMethod({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);
        setRows(response?.data?.data || []);
        setCount(response?.data?.count);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllList();
  }, [debouncedSearchTerm, page]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Reviews</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Reviews
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder="Search..."
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
        </div>
        <div className="sorting_table">
          <TableComponent
            columns={columns}
            rows={rows}
            count={count}
            page={page}
            onPageChange={handlePageChange}
            loading={isLoading}
          />
        </div>
      </div>
    </MainContainer>
  );
};

export default ManageReviews;
